// Extend this union with one literal per question, as each question-screen ticket adds it.
export type QuestionId =
  | 'postalCode'
  | 'name'
  | 'phone'
  | 'verificationType'
  | 'email'
  | 'services'
  | 'business'
  | 'entity'
  | 'industry'
  | 'problem'
  | 'plan'
  | 'meeting'
  | 'question'
  | 'file';

export type Answer =
  | { status: 'answered'; value: string | string[] }
  | { status: 'skipped' }
  | { status: 'unanswered' };

export type MatcherAnswers = Partial<Record<QuestionId, Answer>>;

// Extend this union with one literal per step, as each question-screen ticket adds it.
export type StepId =
  | 'location'
  | 'name'
  | 'phone'
  | 'verification'
  | 'email'
  | 'services'
  | 'business'
  | 'entity'
  | 'industry'
  | 'problem'
  | 'plan'
  | 'meeting'
  | 'question'
  | 'file'
  | 'search';

// One AI-profile tag this option contributes to the /api/matching/search request's terms map
// once selected - category is the dotted fieldName rt-domain-pro-service expects (see
// MatchingSearchTest's searchTermsData.json), code is one of that category's values.
export interface TermValue {
  category: string;
  code: string;
}

// Shared shape for every multi-select checkbox step's options array (see
// MatcherGenericCheckboxStep and docs/form-flow-design.md pattern 2) - one type instead of
// each step file re-declaring an identical interface.
export interface MatcherCheckboxOption {
  id: string;
  label: string;
  followUpSteps: StepId[];
  // Required, same as followUpSteps - a new option can't be added without deciding whether it
  // maps to an AI-profile tag, even if the honest answer is [] (e.g. "Not sure").
  termValues: TermValue[];
}

// One entry in the industry step's searchable list (see flow/industryOptions.ts and
// flow/filterIndustries.ts). code is the value stored on Answer.value when the fan picks this
// suggestion; keywords are extra terms filterIndustries' Fuse.js fallback stage matches against
// (e.g. "food" for a "Restaurants & Food Service" label) without cluttering the visible label.
export interface IndustryOption {
  code: string;
  label: string;
  keywords?: string[];
}

export function getAnsweredValue(
  answer: Answer | undefined
): string | string[] | undefined {
  return answer?.status === 'answered' ? answer.value : undefined;
}

export function getAnsweredString(answer: Answer | undefined): string {
  const value = getAnsweredValue(answer);
  return typeof value === 'string' ? value : '';
}
